import { useEffect, useState, Fragment } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { AiChat } from "@/components/AiChat"
import { PricingModal } from "@/components/subscription/PricingModal"
import { supabase } from "@/integrations/supabase/client"
import { TodayStats } from "@/components/TodayStats"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { MobileNav } from "@/components/MobileNav"
import { useChat } from "@/hooks/use-chat"
import { useChatSidebar } from "@/contexts/ChatSidebarContext"
import { Calendar, ChevronRight, BarChart3 } from "lucide-react"
import { useCalendarEvents } from "@/hooks/use-calendar-events"
import { useSession } from "@/hooks/use-session"
import { format } from "date-fns"
import { ChatLayout } from "@/components/chat/ChatLayout"
import { ChatSidebar } from "@/components/chat/ChatSidebar"

type Volatility = 'high' | 'medium' | 'low';

const Chat = () => {
  const navigate = useNavigate()
  const location = useLocation();
  const [showPricingModal, setShowPricingModal] = useState(false)
  const [isTrialExpired, setIsTrialExpired] = useState(false)
  const isMobile = useIsMobile()
  const { session } = useSession()
  const { closeSidebar } = useChatSidebar()
  
  // Market stats state
  const [todayStats, setTodayStats] = useState({ 
    events: 0, 
    score: 0,
    volatility: 'low' as Volatility
  });
  
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  
  // Fetch calendar events data
  const { data: calendarData } = useCalendarEvents(
    today,
    undefined,
    session,
    {
      enabled: !!session?.user?.id,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
    }
  );
  
  // Update today's stats when calendar data changes
  useEffect(() => {
    if (calendarData?.month) {
      const todayData = calendarData.month[todayStr];
      
      if (todayData) {
        setTodayStats({
          events: todayData.events?.length || 0,
          score: todayData.display_score || 0,
          volatility: (todayData.volatility as Volatility) || 'low'
        });
      }
    }
  }, [calendarData, todayStr]);
  
  // Close global sidebar when entering chat page
  useEffect(() => {
    closeSidebar();
  }, [closeSidebar]);
  
  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Get session from secure cookies
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[Chat] Error fetching session:', sessionError);
          throw sessionError;
        }
        
        // If no session or no user, exit early
        if (!sessionData.session?.user) {
          console.log('[Chat] No authenticated user found');
          return;
        }
        
        const user = sessionData.session.user;
        
        // Fetch profile with maybeSingle to handle missing profile cases
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('trial_end_date')
          .eq('id', user.id)
          .maybeSingle();
          
        if (profileError) {
          console.error('[Chat] Error fetching profile:', profileError);
          // Continue anyway to allow access - we'll validate with the edge function
        }

        const trialEndDate = profile?.trial_end_date ? new Date(profile.trial_end_date) : null;
        const isExpired = trialEndDate ? new Date() > trialEndDate : false;

        if (isExpired) {
          console.log('[Chat] Trial appears expired, checking subscription status');
          const { data, error } = await supabase.functions.invoke('check-subscription', {
            // Include auth headers (should be automatically handled with cookies)
          });
          
          if (error) {
            console.error('[Chat] Error checking subscription:', error);
            throw error;
          }

          if (!data?.subscribed) {
            console.log('[Chat] Subscription check failed - showing pricing modal');
            setIsTrialExpired(true);
            setShowPricingModal(true);
          }
        }
      } catch (error) {
        console.error('[Chat] Error in access check:', error);
        // If this is a CORS or cookie-related error, log it specifically
        if (error instanceof Error && 
            (error.message.includes('cors') || 
             error.message.includes('CORS') || 
             error.message.includes('cookie'))) {
          console.error('[Chat] Possible CORS/cookie issue:', error.message);
        }
      }
    };

    checkAccess();
  }, [])

  const handleStatsClick = () => {
    console.log("Chat: Navigating to calendar view")
    navigate("/calendar")
  }

  if (isTrialExpired && showPricingModal) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <PricingModal 
          open={showPricingModal} 
          onOpenChange={setShowPricingModal}
          isTrialExpired={isTrialExpired}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen relative overflow-hidden bg-gradient-to-b from-[#0c1221] to-[#0a0e18]">
      {/* Header removed for cleaner UI */}
      
      {/* Main content with integrated history sidebar */}
      <div className={cn("flex-1 overflow-hidden relative", isMobile && "pb-16")}>
        {/* Background gradient effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/5 to-transparent pointer-events-none" />
        
        {/* Chat Layout with integrated sidebar */}
        <ChatLayout sidebar={<ChatSidebar />}>
          <AiChat initialQuestion={location.state?.initialQuestion} />
        </ChatLayout>
      </div>
      
      {isMobile && <MobileNav />}
    </div>
  );
};

export default Chat;
